import { useCallback, useEffect, useRef, useState } from 'react';

// useComparePins — manages the set of parcel IDs the user has pinned
// for side-by-side comparison (#147 slice 2).
//
// The compare workflow had a dead-end shape before this: you could
// hit the multi-select Compare on /properties or click "Compare" on
// the parcel detail page, but there was no way to *accumulate* picks
// across navigations — every time you left the page your selection
// was lost. The pin set lives in localStorage so it survives refresh
// and cross-page navigation; the ComparePinBar surfaces it at the
// bottom of the viewport so the user always knows what's pinned and
// can promote to /compare in one click.
//
// Listens for `storage` events so multi-tab edits (pin in one tab,
// see the chip appear in another) stay in sync. Capped at 5 pins
// because the /compare route caps there too — adding past 5 just
// pushes the oldest off the bottom (FIFO) so the user can keep
// picking without explicit "unpin one first" friction.

// Legacy storage key (set when the hook first shipped in #147 slice 2).
// The spec-named key (#789) is the new canonical name; we read either
// to stay back-compat with already-pinned in-flight users.
const STORAGE_KEY = 'landai_compare_pins';
const SPEC_STORAGE_KEY = 'landai.compare.basket';
export const MAX_PINS = 5;

function readFromStorage() {
  try {
    // Prefer the spec-named key when it exists. Fall back to legacy.
    const raw = localStorage.getItem(SPEC_STORAGE_KEY)
              ?? localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((x) => typeof x === 'string').slice(0, MAX_PINS);
    }
  } catch { /* fall through to empty */ }
  return [];
}

// Per-instance source IDs so the same-tab broadcast can filter out
// self-events. Without this, the persist `useEffect` writes →
// dispatches the custom event → the same instance's listener fires
// `setPins` → the persist effect runs again, looping until React's
// max-update-depth guard kills the tree.
let _instanceCounter = 0;
function nextInstanceId() {
  // eslint-disable-next-line no-plusplus
  _instanceCounter += 1;
  return `useComparePins#${_instanceCounter}`;
}

function writeToStorage(arr, sourceId) {
  try {
    const payload = JSON.stringify(arr);
    // Write both keys so the spec-named compareBasket helpers see the
    // same set, AND so any older tab that still reads the legacy key
    // (older client builds, slice-2 ComparePinBar before #789) keeps
    // working seamlessly.
    localStorage.setItem(STORAGE_KEY, payload);
    localStorage.setItem(SPEC_STORAGE_KEY, payload);
    // Same-tab notify for OTHER useComparePins hook instances. The
    // browser-native storage event only fires on OTHER tabs, so without
    // this any sibling React component reading through its own
    // useComparePins instance won't see the write until it re-renders
    // for some other reason. `source` lets each instance filter out
    // the event it just dispatched.
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('landai-compare-basket-changed', {
          detail: { basket: arr, source: sourceId },
        }));
      } catch { /* CustomEvent unavailable */ }
    }
  } catch { /* localStorage may be unavailable in some contexts */ }
}

export default function useComparePins() {
  const [pins, setPins] = useState(readFromStorage);
  // Stable per-instance ID so the same-tab broadcast can ignore the
  // event this very instance just emitted (otherwise the persist
  // useEffect → setPins → persist again loop).
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = nextInstanceId();
  }

  // Persist on every change.
  useEffect(() => {
    writeToStorage(pins, instanceIdRef.current);
  }, [pins]);

  // Multi-tab sync — the `storage` event fires on OTHER tabs when
  // this tab writes, and vice versa. Without this, opening two tabs
  // would let them drift out of sync silently.
  // Same-tab sync — `landai-compare-basket-changed` is dispatched by
  // every write so sibling hook instances (e.g. the ComparePinBar and
  // a button on the same page) refresh their `pins` state in the same
  // tick instead of waiting for an unrelated re-render. Self-events
  // are filtered via `detail.source`.
  useEffect(() => {
    const sameArr = (a, b) => (
      a.length === b.length && a.every((v, i) => v === b[i])
    );
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY && e.key !== SPEC_STORAGE_KEY) return;
      const fresh = readFromStorage();
      setPins((prev) => (sameArr(prev, fresh) ? prev : fresh));
    };
    const onCustom = (e) => {
      if (e && e.detail && e.detail.source === instanceIdRef.current) return;
      // Pull from the event payload when available so we don't have to
      // re-parse storage just to compare. Both event-pong instances
      // would otherwise loop forever on equal-but-new-reference arrays
      // — explicitly bail when the payload matches the current state.
      const fresh = (e && e.detail && Array.isArray(e.detail.basket))
        ? e.detail.basket : readFromStorage();
      setPins((prev) => (sameArr(prev, fresh) ? prev : fresh));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('landai-compare-basket-changed', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('landai-compare-basket-changed', onCustom);
    };
  }, []);

  const pin = useCallback((parcelId) => {
    if (!parcelId) return;
    setPins((prev) => {
      if (prev.includes(parcelId)) return prev;
      // FIFO at the cap so the bar stays clear of stale picks.
      const next = [...prev, parcelId];
      return next.length > MAX_PINS ? next.slice(next.length - MAX_PINS) : next;
    });
  }, []);

  const unpin = useCallback((parcelId) => {
    setPins((prev) => prev.filter((id) => id !== parcelId));
  }, []);

  const toggle = useCallback((parcelId) => {
    if (!parcelId) return;
    setPins((prev) => (
      prev.includes(parcelId)
        ? prev.filter((id) => id !== parcelId)
        : (prev.length >= MAX_PINS
            ? [...prev.slice(1), parcelId]
            : [...prev, parcelId])
    ));
  }, []);

  const clear = useCallback(() => setPins([]), []);

  const isPinned = useCallback((parcelId) => pins.includes(parcelId), [pins]);

  return { pins, pin, unpin, toggle, clear, isPinned };
}
